import { ModalProps } from '@mantine/core';
import { forwardRef } from 'react';
import { ModalFormModel } from 'shared/lib';
import { createModalFormActionIcon } from './modal-form-action-icon';
import { createModalFormBody } from './modal-form-body';
import { createModalFormButton } from './modal-form-button';
import {
	createModalFormCancelButton,
	ModalFormCancelButtonProps,
} from './modal-form-cancel-button';
import { createModalFormCloseButton } from './modal-form-close-button';
import { createModalFormContent } from './modal-form-content';
import { createModalFormFooter } from './modal-form-footer';
import { createModalFormForm } from './modal-form-form';
import { createModalFormHeader } from './modal-form-header';
import { createModalFormOverlay } from './modal-form-overlay';
import { createModalFormRoot } from './modal-form-root';
import {
	createModalFormSubmitButton,
	ModalFormSubmitButtonProps,
} from './modal-form-submit-button';
import { createModalFormTitle } from './modal-form-title';

export type ModalFormProps = Omit<ModalProps, 'opened' | 'onClose'> & {
	cancelButtonProps?: ModalFormCancelButtonProps;
	submitButtonProps?: ModalFormSubmitButtonProps;
	variant?: 'default' | 'confirm' | 'submit';
};

export const createModalForm = <TModalData, TFormValues extends Record<string, unknown>>(
	modalFormModel: ModalFormModel<TModalData, TFormValues>,
) => {
	const ModalFormRoot = createModalFormRoot(modalFormModel);
	const ModalFormOverlay = createModalFormOverlay();
	const ModalFormContent = createModalFormContent();
	const ModalFormForm = createModalFormForm(modalFormModel);
	const ModalFormHeader = createModalFormHeader();
	const ModalFormTitle = createModalFormTitle();
	const ModalFormCloseButton = createModalFormCloseButton();
	const ModalFormBody = createModalFormBody();
	const ModalFormFooter = createModalFormFooter();
	const ModalFormCancelButton = createModalFormCancelButton(modalFormModel);
	const ModalFormSubmitButton = createModalFormSubmitButton();
	const ModalFormButton = createModalFormButton(modalFormModel);
	const ModalFormActionIcon = createModalFormActionIcon(modalFormModel);

	const ModalFormBase = forwardRef<HTMLDivElement, ModalFormProps>(
		(
			{
				title,
				withCloseButton = true,
				withOverlay = true,
				overlayProps,
				closeButtonProps,
				cancelButtonProps,
				submitButtonProps,
				children,
				...props
			},
			ref,
		) => (
			<ModalFormRoot
				ref={ref}
				{...props}
			>
				{withOverlay && <ModalFormOverlay {...overlayProps} />}
				<ModalFormContent>
					{withCloseButton && (
						<ModalFormHeader>
							<ModalFormTitle>{title}</ModalFormTitle>
							<ModalFormCloseButton {...closeButtonProps} />
						</ModalFormHeader>
					)}
					<ModalFormForm>
						<ModalFormBody>{children}</ModalFormBody>
						<ModalFormFooter>
							{cancelButtonProps && <ModalFormCancelButton {...cancelButtonProps} />}
							{submitButtonProps && <ModalFormSubmitButton {...submitButtonProps} />}
						</ModalFormFooter>
					</ModalFormForm>
				</ModalFormContent>
			</ModalFormRoot>
		),
	);

	return Object.assign(ModalFormBase, {
		Root: ModalFormRoot,
		Overlay: ModalFormOverlay,
		Content: ModalFormContent,
		Form: ModalFormForm,
		Header: ModalFormHeader,
		Title: ModalFormTitle,
		CloseButton: ModalFormCloseButton,
		Body: ModalFormBody,
		Footer: ModalFormFooter,
		CancelButton: ModalFormCancelButton,
		SubmitButton: ModalFormSubmitButton,
		Button: ModalFormButton,
		ActionIcon: ModalFormActionIcon,
	});
};
